import { WEEKDAYS } from '@/constants/weekdays';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { TarificationFormData } from '@/types/benefits/tarification/TarificationFormData';
import {
  BenefitFormData,
  CircuitDayFormData,
} from '@/types/circuits/CircuitDaysFormData';
import { getBenefitTypeName } from '@/utils/benefits/getBenefitTypeName';
import { addDays, getDay, isAfter, isBefore } from 'date-fns';
import {
  priceDateKey,
  priceKeyToDate,
  storedDateToLocalDate,
} from '@/utils/circuits/prices/priceDateKey';
import { loadVehicleDayPriceFromFormula } from './loadVehicleDayPriceFromFormula';
import { roundUpPrice } from './formatPrice';
import { getDefaultPricingWindow, PricingWindow } from './pricingHorizon';
import { computeHotelPricesForDates } from '@/utils/estimates/hotel/computeHotelRoomPrice';
import { getHotelSupplementsTotalForDate } from '@/utils/estimates/hotel/computeHotelSupplementPrice';
import { toHotelPensionType } from '@/constants/hotelPension';
import type { HotelSupplement } from '@/types/benefits/hotel/HotelResponse';
import type { AssociatedBenefitFrequency } from '@/types/benefits/associated-benefit-frequency';
import { CarOneWayFee, CarTax } from '@/types/circuits/CircuitResponse';
import {
  buildHotelPensionPriceMap,
  resolvePensionPricings,
} from '@/utils/estimates/hotel/computeHotelPensionPrice';

export type PriceWithFormula = {
  minimumPrice: number;
  formula: string;
};

export type BenefitPriceWithFormula = PriceWithFormula & {
  marginRate: number;
};

export type CircuitDayPrices = ReturnType<typeof getCircuitDayPricesObject>;

export const computeBenefitMarginRate = ({
  benefitMarginRate,
  circuitMarginRate,
  supplierMarginRate,
}: {
  benefitMarginRate?: number;
  supplierMarginRate?: number;
  circuitMarginRate?: number;
}) => (benefitMarginRate ?? supplierMarginRate ?? circuitMarginRate ?? 0) / 100;

const computePriceAfterMargin = (price: BenefitPriceWithFormula, nbDays = 1) =>
  roundUpPrice((price.minimumPrice / nbDays) * (1 + price.marginRate / 100));

const extractDayPrice = (
  stepElement: CircuitDayPrices[number]['transport'],
  day: Date,
) =>
  stepElement?.standardBenefit
    ? (stepElement.standardBenefit.prices.get(priceDateKey(day)) ?? {
        minimumPrice: Infinity,
        formula: '',
      })
    : {
        minimumPrice: 0,
        formula: '',
      };

export const getCircuitDayTotalPrice = ({
  circuitDayPrices,
  day,
  targetCurrency = 'EUR',
  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  convertPrice = (x, _, __) => x,
}: {
  circuitDayPrices: CircuitDayPrices;
  day: Date;
  targetCurrency?: string;
  convertPrice?: (
    price: number,
    fromCurrency: string,
    toCurrency: string,
  ) => number;
}) => {
  return circuitDayPrices.reduce(
    (acc, { transport, meals, accommodations, activities }) =>
      acc +
      convertPrice(
        computePriceAfterMargin({
          ...extractDayPrice(transport, day),
          marginRate: transport?.standardBenefit?.marginRate ?? 0,
        }),
        transport?.standardBenefit?.currency ?? targetCurrency,
        targetCurrency,
      ) +
      convertPrice(
        computePriceAfterMargin({
          ...extractDayPrice(meals, day),
          marginRate: meals?.standardBenefit?.marginRate ?? 0,
        }),
        meals?.standardBenefit?.currency ?? targetCurrency,
        targetCurrency,
      ) +
      convertPrice(
        computePriceAfterMargin({
          ...extractDayPrice(accommodations, day),
          marginRate: accommodations?.standardBenefit?.marginRate ?? 0,
        }),
        accommodations?.standardBenefit?.currency ?? targetCurrency,
        targetCurrency,
      ) +
      convertPrice(
        computePriceAfterMargin({
          ...extractDayPrice(activities, day),
          marginRate: activities?.standardBenefit?.marginRate ?? 0,
        }),
        activities?.standardBenefit?.currency ?? targetCurrency,
        targetCurrency,
      ),
    0,
  );
};

const extractPricesFromStepElement = ({
  stepElement,
  nbParticipants,
  circuitMarginRate,
  nbChildren = 0,
  nbAdolescents = 0,
  childrenAges = [],
  indexDay,
  targetDates,
  pricingWindow,
}: {
  stepElement: CircuitDayFormData['steps'][number]['transport'];
  nbParticipants: number;
  circuitMarginRate?: number;
  nbChildren?: number;
  nbAdolescents?: number;
  childrenAges?: number[];
  indexDay: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  return {
    standardBenefit: stepElement.standardBenefit
      ? {
          ...computeBenefitPrices({
            benefit: stepElement.standardBenefit,
            selectedHotelPensionType:
              stepElement.standardBenefitPensionType ??
              stepElement.standardBenefit.hotel?.pricingPensionType ??
              undefined,
            nbParticipants,
            circuitMarginRate,
            nbDays: stepElement.nbDays ? Number(stepElement.nbDays) : undefined,
            nbChildren,
            nbAdolescents,
            indexDay,
            childrenAges,
            targetDates,
            pricingWindow,
          }),
          currency: stepElement.standardBenefit.currency,
        }
      : undefined,
    optionalBenefits: stepElement.optionalBenefits
      ? stepElement.optionalBenefits.map((benefit) => ({
          ...computeBenefitPrices({
            benefit,
            selectedHotelPensionType:
              stepElement.optionalBenefitsPensionTypes?.[benefit.id] ??
              benefit.hotel?.pricingPensionType ??
              undefined,
            nbParticipants,
            circuitMarginRate,
            nbDays: stepElement.nbDays ? Number(stepElement.nbDays) : undefined,
            nbChildren,
            nbAdolescents,
            indexDay,
            childrenAges,
            targetDates,
            pricingWindow,
          }),
          currency: benefit.currency,
        }))
      : undefined,
  };
};

export const getCircuitDayPricesObject = <
  T extends { steps: CircuitDayFormData['steps'] },
>({
  circuitDay,
  nbParticipants,
  circuitMarginRate,
  nbChildren = 0,
  nbAdolescents = 0,
  childrenAges = [],
  indexDay,
  targetDates,
  pricingWindow,
}: {
  circuitDay: T;
  nbParticipants: number;
  circuitMarginRate?: number;
  nbChildren?: number;
  nbAdolescents?: number;
  childrenAges?: number[];
  indexDay: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const extractPrices = (
    stepElement: CircuitDayFormData['steps'][number]['transport'],
  ) =>
    extractPricesFromStepElement({
      stepElement,
      nbParticipants,
      circuitMarginRate,
      nbChildren,
      nbAdolescents,
      childrenAges,
      indexDay,
      targetDates,
      pricingWindow,
    });

  return circuitDay.steps.map((step) => ({
    transport: step.transport ? extractPrices(step.transport) : undefined,
    activities: step.activities ? extractPrices(step.activities) : undefined,
    meals: step.meals ? extractPrices(step.meals) : undefined,
    accommodations: step.accommodations
      ? extractPrices(step.accommodations)
      : undefined,
  }));
};

export const computeBenefitPrices = ({
  benefit,
  selectedHotelPensionType,
  nbParticipants,
  nbDays = 1,
  circuitMarginRate,
  nbChildren = 0,
  nbAdolescents = 0,
  childrenAges = [],
  indexDay,
  targetDates,
  pricingWindow,
}: {
  benefit: BenefitFormData;
  selectedHotelPensionType?: string;
  nbParticipants: number;
  nbDays?: number;
  circuitMarginRate?: number;
  nbChildren?: number;
  nbAdolescents?: number;
  childrenAges?: number[];
  indexDay: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const benefitType = getBenefitTypeName(benefit.type);

  const marginRate =
    computeBenefitMarginRate({
      benefitMarginRate: benefit.marginRate,
      circuitMarginRate,
      supplierMarginRate: benefit.supplier.marginRate,
    }) * 100;

  const hotelNbChildren =
    childrenAges.length &&
    benefit.hotel &&
    benefit.hotel.acceptsChildPricing &&
    benefit.hotel.childAgeFrom &&
    benefit.hotel.childAgeTo
      ? childrenAges.filter(
          (age) =>
            age >= (benefit.hotel?.childAgeFrom ?? 2) &&
            age <= (benefit.hotel?.childAgeTo ?? 11),
        ).length
      : nbChildren;

  const hotelNbAdolescents =
    childrenAges.length &&
    benefit.hotel &&
    benefit.hotel.acceptsChildPricing &&
    benefit.hotel.secondChildAgeFrom &&
    benefit.hotel.secondChildAgeTo
      ? childrenAges.filter(
          (age) =>
            age >= (benefit.hotel?.secondChildAgeFrom ?? 12) &&
            age <= (benefit.hotel?.secondChildAgeTo ?? 17),
        ).length
      : nbAdolescents;

  const activityNbChildren =
    childrenAges.length &&
    benefit.activity?.specialTarif &&
    benefit.activity.childAgeFrom &&
    benefit.activity.childAgeTo
      ? childrenAges.filter(
          (age) =>
            age >= (benefit.activity?.childAgeFrom ?? 2) &&
            age <= (benefit.activity?.childAgeTo ?? 11),
        ).length
      : nbChildren;

  const flightNbChildren =
    childrenAges.length &&
    benefit.flight?.specialTarif &&
    benefit.flight.childAgeFrom &&
    benefit.flight.childAgeTo
      ? childrenAges.filter(
          (age) =>
            age >= (benefit.flight?.childAgeFrom ?? 2) &&
            age <= (benefit.flight?.childAgeTo ?? 11),
        ).length
      : nbChildren;

  switch (benefitType) {
    case BenefitType.ACTIVITY:
    case BenefitType.TRANSFER:
    case BenefitType.MEAL:
      return {
        prices: computeActivityPrices({
          pricingType:
            benefit.activity?.pricingType === 'byPerson'
              ? 'byPerson'
              : 'byBenefit',
          tarifs: benefit.Pricing,
          nbParticipants,
          nbChildren: activityNbChildren,
          targetDates,
          pricingWindow,
        }),
        marginRate,
      };

    case BenefitType.VEHICLE_WITH_CHAUFFEUR:
      return {
        prices: computeVehicleWithChauffeurPrices({
          nbParticipants,
          tarifs: benefit.Pricing,
          maxCapacity: benefit.VehicleWithChauffeur?.maxCapacity ?? 4,
          nbDays,
          indexFirstDay: benefit.indexFirstDay ?? undefined,
          indexCurrentDay: indexDay,
          targetDates,
          pricingWindow,
        }),
        marginRate,
      };

    case BenefitType.VOL:
      return {
        prices: computeFlightPrices({
          nbParticipants,
          tarifs: benefit.Pricing,
          nbChildren: flightNbChildren,
          targetDates,
          pricingWindow,
        }),
        marginRate,
      };

    case BenefitType.VEHICLE:
      return {
        prices: computeCarRentalPrices({
          nbParticipants,
          carCategories: benefit.carRental?.carCategories ?? [],
          nbDays,
          indexFirstDay: benefit.indexFirstDay ?? undefined,
          indexCurrentDay: indexDay,
          targetDates,
          pricingWindow,
        }),
        marginRate,
      };

    case BenefitType.HOTEL:
      return {
        prices: computeHotelPrices({
          nbAdults: nbParticipants - (hotelNbChildren + hotelNbAdolescents),
          nbChildren: hotelNbChildren,
          nbAdolescents: hotelNbAdolescents,
          pricingType: benefit.hotel?.pricingType,
          hotelCategories: benefit.hotel?.HotelRoomCategory ?? [],
          isReduction: benefit.hotel?.isReduction ?? false,
          reducedPriceExists: benefit.hotel?.reducedPriceExists ?? false,
          hotelSupplements:
            benefit.hotel?.shouldFillHotelSupplements &&
            benefit.hotel?.hotelSupplements?.length
              ? (benefit.hotel.hotelSupplements as HotelSupplement[])
              : undefined,
          commonPensionPricings: benefit.hotel?.commonPensionPricings ?? [],
          categoryPensionPricings: benefit.hotel?.HotelRoomCategory?.flatMap(
            (category) => category.categoryPensionPricings ?? [],
          ),
          areOtherPensionPricesCommon:
            benefit.hotel?.areOtherPensionPricesCommon ?? false,
          pricingPensionType:
            selectedHotelPensionType ??
            benefit.hotel?.pricingPensionType ??
            undefined,
          associatedBenefits: (() => {
            const h = benefit.hotel as
              | { shouldAssociateBenefits?: boolean | string }
              | undefined;
            if (
              h?.shouldAssociateBenefits !== 'true' &&
              h?.shouldAssociateBenefits !== true
            )
              return undefined;
            return benefit.hotel?.associatedBenefits?.length
              ? benefit.hotel.associatedBenefits
              : undefined;
          })(),
          nbParticipants,
          activityNbChildren,
          indexDay,
          indexFirstDay: benefit.indexFirstDay ?? undefined,
          targetDates,
          pricingWindow,
        }),
        marginRate,
      };
  }
};

export const getPricingRangeForPeriod = ({
  periodStart,
  periodEnd,
  today,
  lastDayOfPricingWindow,
  targetDates,
}: {
  periodStart: Date;
  periodEnd: Date;
  today: Date;
  lastDayOfPricingWindow: Date;
  targetDates?: Date[];
}): [Date, Date] => {
  const baseRangeStart = new Date(
    Math.max(periodStart.getTime(), today.getTime()),
  );
  const baseRangeEnd = new Date(
    Math.min(periodEnd.getTime(), lastDayOfPricingWindow.getTime()),
  );

  const inPeriodTargetDates =
    targetDates?.filter(
      (d) => !isBefore(d, periodStart) && !isAfter(d, periodEnd),
    ) ?? [];

  if (inPeriodTargetDates.length === 0) return [baseRangeStart, baseRangeEnd];

  const minTarget = new Date(
    Math.min(...inPeriodTargetDates.map((d) => d.getTime())),
  );
  const maxTarget = new Date(
    Math.max(...inPeriodTargetDates.map((d) => d.getTime())),
  );
  const expandedStart = new Date(
    Math.min(baseRangeStart.getTime(), minTarget.getTime()),
  );
  const expandedEnd = new Date(
    Math.max(baseRangeEnd.getTime(), maxTarget.getTime()),
  );

  return [
    isBefore(expandedStart, periodStart) ? periodStart : expandedStart,
    isAfter(expandedEnd, periodEnd) ? periodEnd : expandedEnd,
  ];
};

const computePricesForDates = ({
  tarifs,
  nbParticipants,
  processPartitionsFn,
  targetDates,
  pricingWindow,
}: {
  tarifs: TarificationFormData[];
  nbParticipants: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
  processPartitionsFn: ({
    nbParticipants,
    partitions,
  }: {
    nbParticipants: number;
    partitions: TarificationFormData['pricePartitions'];
  }) => PriceWithFormula;
}) => {
  const pricesForDate = new Map<string, PriceWithFormula>();
  const { start: today, end: lastDayOfPricingWindow } =
    pricingWindow ?? getDefaultPricingWindow();

  tarifs.forEach((tarif) => {
    tarif.periods.forEach((period) => {
      const periodStart = storedDateToLocalDate(period.startDate);
      const periodEnd = storedDateToLocalDate(period.endDate);

      const [rangeStart, rangeEnd] = getPricingRangeForPeriod({
        periodStart,
        periodEnd,
        today,
        lastDayOfPricingWindow,
        targetDates,
      });

      if (isAfter(rangeStart, rangeEnd)) return;

      let currDate = rangeStart;
      const dayAfterEndDate = addDays(rangeEnd, 1);

      while (isBefore(currDate, dayAfterEndDate)) {
        const dayIndex = (getDay(currDate) + 6) % 7;
        const dayName = WEEKDAYS[dayIndex];

        if (period.applicationDays.includes(dayName)) {
          const priceWithFormula = processPartitionsFn({
            nbParticipants,
            partitions: tarif.pricePartitions,
          });

          const prevPrice = pricesForDate.get(priceDateKey(currDate));
          if (
            !prevPrice ||
            priceWithFormula.minimumPrice < prevPrice.minimumPrice
          ) {
            pricesForDate.set(priceDateKey(currDate), priceWithFormula);
          }
        }

        currDate = addDays(currDate, 1);
      }
    });
  });

  return pricesForDate;
};

const computeActivityPrices = ({
  pricingType,
  tarifs,
  nbParticipants = 1,
  nbChildren = 0,
  targetDates,
  pricingWindow,
}: {
  pricingType?: 'byPerson' | 'byBenefit';
  tarifs: TarificationFormData[];
  nbParticipants?: number;
  nbChildren?: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = computePricesForDates({
    tarifs,
    nbParticipants,
    processPartitionsFn: ({ partitions, nbParticipants }) =>
      findMinimumPrice({
        nbParticipants,
        pricePartitions: partitions,
        pricingType,
        nbChildren,
      }),
    targetDates,
    pricingWindow,
  });

  return pricesForDate;
};

const computeFlightPrices = ({
  nbParticipants,
  tarifs,
  nbChildren = 0,
  targetDates,
  pricingWindow,
}: {
  nbParticipants: number;
  tarifs: TarificationFormData[];
  nbChildren?: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = computePricesForDates({
    tarifs,
    nbParticipants,
    processPartitionsFn: ({ nbParticipants, partitions }) =>
      findMinimumPrice({
        nbParticipants,
        pricePartitions: partitions,
        pricingType: 'byPerson',
        nbChildren,
      }),
    targetDates,
    pricingWindow,
  });

  return pricesForDate;
};

const computeVehicleWithChauffeurPrices = ({
  nbParticipants,
  tarifs,
  maxCapacity,
  nbDays,
  indexFirstDay,
  indexCurrentDay,
  targetDates,
  pricingWindow,
}: {
  nbParticipants: number;
  tarifs: TarificationFormData[];
  maxCapacity: number;
  nbDays: number;
  indexFirstDay?: number;
  indexCurrentDay: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = computePricesForDates({
    tarifs,
    nbParticipants,
    processPartitionsFn: ({ nbParticipants, partitions }) =>
      findMinimumPriceForDays({
        nbParticipants,
        maxCapacity,
        nbDays,
        partitions,
      }),
    targetDates,
    pricingWindow,
  });

  return new Map(
    Array.from(pricesForDate).map(([date, priceWithFormula]) => {
      const newPriceWithFormula = priceWithFormula
        ? indexFirstDay !== undefined &&
          indexCurrentDay !== undefined &&
          indexCurrentDay >= indexFirstDay &&
          indexFirstDay >= 0
          ? (loadVehicleDayPriceFromFormula(priceWithFormula.formula)[
              indexCurrentDay - indexFirstDay
            ] ?? {
              formula: '',
              minimumPrice: 0,
            })
          : priceWithFormula
        : { formula: '', minimumPrice: 0 };
      return [date, newPriceWithFormula];
    }),
  );
};

const computeCarRentalPrices = ({
  carCategories,
  nbDays,
  nbParticipants,
  indexFirstDay,
  indexCurrentDay,
  targetDates,
  pricingWindow,
}: {
  carCategories: {
    maxCapacity?: number;
    pricings: TarificationFormData[];
    id?: string | null;
    taxes?: CarTax[];
    oneWayFees?: CarOneWayFee[];
    displayCategoryOptions?: boolean | null;
  }[];
  nbDays: number;
  nbParticipants: number;
  indexFirstDay?: number;
  indexCurrentDay: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = new Map<string, PriceWithFormula>();
  const selectedCategoryIdByDate = new Map<string, string>();

  carCategories.forEach(({ pricings, maxCapacity, id: categoryId }) => {
    const categoryPricesForDate = computePricesForDates({
      nbParticipants,
      tarifs: pricings,
      processPartitionsFn: ({ partitions, nbParticipants }) =>
        findMinimumPriceForDays({
          maxCapacity: maxCapacity ?? 4,
          nbDays,
          partitions,
          nbParticipants,
        }),
      targetDates,
      pricingWindow,
    });
    categoryPricesForDate.forEach((price, date) => {
      const prevPrice = pricesForDate.get(date);
      if (!prevPrice || price.minimumPrice < prevPrice.minimumPrice) {
        pricesForDate.set(date, price);
        selectedCategoryIdByDate.set(date, categoryId ?? '');
      }
    });
  });

  return new Map(
    Array.from(pricesForDate).map(([date, priceWithFormula]) => {
      const categoryId = selectedCategoryIdByDate.get(date);
      const category = carCategories.find((c) => c.id === categoryId);
      const taxes = category?.taxes ?? [];
      const oneWayFees = category?.oneWayFees ?? [];
      const nbCars = Math.ceil(nbParticipants / (category?.maxCapacity ?? 4));

      const totalTaxes = taxes
        .filter((tax) => tax.required)
        .reduce((acc, tax) => {
          const billingType = tax.billingType ?? 'perDay';
          if (billingType === 'perDay')
            return acc + roundUpPrice((tax.amount ?? 0) * nbCars * nbDays);
          return acc + roundUpPrice((tax.amount ?? 0) * nbCars);
        }, 0);
      const totalOneWayFees = oneWayFees.reduce((acc, oneWayFee) => {
        if (!oneWayFee) return acc;
        return acc + roundUpPrice((oneWayFee?.amount ?? 0) * nbCars);
      }, 0);

      const newPriceWithFormula = priceWithFormula
        ? indexFirstDay !== undefined &&
          indexCurrentDay !== undefined &&
          indexCurrentDay >= indexFirstDay &&
          indexFirstDay >= 0
          ? (loadVehicleDayPriceFromFormula(priceWithFormula.formula)[
              indexCurrentDay - indexFirstDay
            ] ?? {
              formula: '',
              minimumPrice: 0,
            })
          : priceWithFormula
        : { formula: '', minimumPrice: 0 };

      if (
        priceWithFormula &&
        indexFirstDay !== undefined &&
        indexCurrentDay !== undefined &&
        indexCurrentDay === indexFirstDay &&
        indexFirstDay >= 0
      ) {
        const totalPriceWithTaxesAndOneWayFees =
          newPriceWithFormula.minimumPrice + totalTaxes + totalOneWayFees;
        newPriceWithFormula.minimumPrice = totalPriceWithTaxesAndOneWayFees;
        newPriceWithFormula.formula = `${newPriceWithFormula.formula} + ${totalTaxes} + ${totalOneWayFees}`;
      }

      return [date, newPriceWithFormula];
    }),
  );
};

export const computeHotelPrices = ({
  hotelCategories,
  nbAdults,
  pricingType,
  reducedPriceExists = false,
  isReduction = false,
  nbAdolescents = 0,
  nbChildren = 0,
  hotelSupplements,
  commonPensionPricings,
  categoryPensionPricings,
  areOtherPensionPricesCommon = false,
  pricingPensionType,
  associatedBenefits,
  nbParticipants,
  activityNbChildren,
  indexDay,
  indexFirstDay,
  targetDates,
  pricingWindow,
}: {
  hotelCategories: Required<BenefitFormData>['hotel']['HotelRoomCategory'];
  nbAdults: number;
  pricingType?: 'byPerson' | 'byRoom';
  reducedPriceExists?: boolean;
  isReduction?: boolean;
  nbAdolescents?: number;
  nbChildren?: number;
  hotelSupplements?: HotelSupplement[];
  commonPensionPricings?: Required<BenefitFormData>['hotel']['commonPensionPricings'];
  categoryPensionPricings?: Required<BenefitFormData>['hotel']['HotelRoomCategory'][number]['categoryPensionPricings'];
  areOtherPensionPricesCommon?: boolean;
  pricingPensionType?: string | null;
  associatedBenefits?: Array<{
    frequency: AssociatedBenefitFrequency;
    benefit?: {
      Pricing?: TarificationFormData[];
      activity?: { pricingType?: 'byPerson' | 'byBenefit' };
    };
  }>;
  nbParticipants?: number;
  activityNbChildren?: number;
  indexDay?: number;
  indexFirstDay?: number;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = new Map<string, PriceWithFormula>();

  hotelCategories.forEach((category) => {
    const categoryPricesForDate = computeHotelPricesForDates({
      category,
      nbAdults,
      pricingType,
      reducedPriceExists,
      isReduction,
      nbAdolescents,
      nbChildren,
      targetDates,
      pricingWindow,
    });

    categoryPricesForDate.forEach((price, date) => {
      const prevPrice = pricesForDate.get(date);
      if (!prevPrice || price.minimumPrice < prevPrice.minimumPrice) {
        pricesForDate.set(date, price);
      }
    });
  });

  if (pricingPensionType) {
    const selectedPensionType = toHotelPensionType(pricingPensionType);
    if (selectedPensionType) {
      const pensionPricings = resolvePensionPricings(
        commonPensionPricings,
        categoryPensionPricings,
        areOtherPensionPricesCommon,
      );
      const pensionPriceMap = buildHotelPensionPriceMap(
        pensionPricings,
        selectedPensionType,
      );
      const withPension = new Map<string, PriceWithFormula>();
      pricesForDate.forEach((roomPrice, dateStr) => {
        const pensionPrice = pensionPriceMap.get(dateStr);
        const pensionTotal =
          (pensionPrice?.adultPrice ?? 0) * nbAdults +
          (pensionPrice?.childPrice ?? 0) * nbChildren +
          (pensionPrice?.adolescentPrice ?? 0) * nbAdolescents;
        withPension.set(dateStr, {
          minimumPrice: roomPrice.minimumPrice + pensionTotal,
          formula:
            pensionTotal > 0
              ? `${roomPrice.formula} + ${pensionTotal}`
              : roomPrice.formula,
        });
      });
      pricesForDate.clear();
      withPension.forEach((v, k) => pricesForDate.set(k, v));
    }
  }

  if (
    hotelSupplements?.length &&
    pricingPensionType != null &&
    pricingPensionType !== ''
  ) {
    const basePensionType = toHotelPensionType(pricingPensionType);
    if (basePensionType) {
      const withSupplements = new Map<string, PriceWithFormula>();
      pricesForDate.forEach((roomPrice, dateStr) => {
        const date = priceKeyToDate(dateStr);
        const supplementTotal = getHotelSupplementsTotalForDate(
          hotelSupplements,
          date,
          basePensionType,
          nbAdults,
          nbChildren,
          nbAdolescents,
        );
        withSupplements.set(dateStr, {
          minimumPrice: roomPrice.minimumPrice + supplementTotal,
          formula:
            supplementTotal > 0
              ? `${roomPrice.formula} + ${supplementTotal}`
              : roomPrice.formula,
        });
      });
      pricesForDate.clear();
      withSupplements.forEach((v, k) => pricesForDate.set(k, v));
    }
  }

  if (
    associatedBenefits?.length &&
    nbParticipants != null &&
    indexDay !== undefined
  ) {
    const firstNightIndex = indexFirstDay ?? 0;
    const isFirstNightOfStay = indexDay === firstNightIndex;

    associatedBenefits.forEach((associatedBenefit) => {
      const pricing = associatedBenefit.benefit?.Pricing;
      if (!pricing?.length) return;
      const pricingTypeAb =
        associatedBenefit.benefit?.activity?.pricingType === 'byPerson'
          ? 'byPerson'
          : 'byBenefit';
      const associatedPrices = computeActivityPrices({
        pricingType: pricingTypeAb,
        tarifs: pricing,
        nbParticipants,
        nbChildren: activityNbChildren ?? 0,
        targetDates,
        pricingWindow,
      });
      pricesForDate.forEach((dayPrice, dateStr) => {
        const addOnce =
          associatedBenefit.frequency === 'ONCE' && isFirstNightOfStay;
        const addEachNight = associatedBenefit.frequency === 'EACH_NIGHT';
        // Per spec: ONCE = first night of hotel only → add to all dates so lookup works for any selected start date
        if (!addOnce && !addEachNight) return;
        const assocPrice = associatedPrices.get(dateStr) ?? {
          minimumPrice: 0,
          formula: '',
        };
        const newMin = dayPrice.minimumPrice + assocPrice.minimumPrice;
        const newFormula =
          assocPrice.minimumPrice > 0
            ? `${dayPrice.formula} + ${assocPrice.formula}`
            : dayPrice.formula;
        pricesForDate.set(dateStr, {
          minimumPrice: newMin,
          formula: newFormula,
        });
      });
    });
  }

  return pricesForDate;
};

export const findMinimumPrice = ({
  nbParticipants,
  pricePartitions,
  pricingType = 'byBenefit',
  nbChildren = 0,
}: {
  nbParticipants: number;
  pricePartitions: TarificationFormData['pricePartitions'];
  pricingType?: 'byPerson' | 'byBenefit';
  nbChildren?: number;
}): PriceWithFormula => {
  const safeNbParticipants = Math.max(
    1,
    Number.isFinite(nbParticipants) ? nbParticipants : 1,
  );
  const safeNbChildren = Math.max(
    0,
    Number.isFinite(nbChildren) ? nbChildren : 0,
  );
  const pricesMemo = Array.from({ length: safeNbParticipants + 1 }, () =>
    new Array(safeNbChildren + 1).fill(Infinity),
  );
  const formulaTracker = Array.from({ length: safeNbParticipants + 1 }, () =>
    new Array(safeNbChildren + 1).fill(''),
  );

  pricesMemo[0] = Array.from({ length: safeNbChildren + 1 }, () => 0);
  formulaTracker[0] = Array.from({ length: safeNbChildren + 1 }, () => '');

  const options: Array<{
    count: number;
    price: number;
    specialPrice?: number;
  }> = [];

  pricePartitions.forEach((partition) => {
    for (let i = partition.from; i <= partition.to; i++) {
      options.push({
        count: i > 0 ? i : 1,
        price: partition.price,
        specialPrice: partition.specialPrice,
      });
    }
  });

  for (let i = 0; i <= safeNbParticipants; i++) {
    for (let j = 0; j <= Math.min(safeNbChildren, i); j++) {
      for (const option of options) {
        const remainingParticipants = i - option.count;

        if (remainingParticipants >= 0) {
          const participantPrice =
            pricingType === 'byPerson'
              ? option.count * option.price
              : option.price;

          if (
            pricesMemo[remainingParticipants][j] + participantPrice <=
            pricesMemo[i][j]
          ) {
            pricesMemo[i][j] =
              pricesMemo[remainingParticipants][j] + participantPrice;
            const partFormula =
              pricingType === 'byPerson' && option.count > 1
                ? `${option.count} × ${option.price}`
                : `${option.price}`;
            formulaTracker[i][j] =
              `${formulaTracker[remainingParticipants][j]}${formulaTracker[remainingParticipants][j] ? ' + ' : ''}${partFormula}`;
          }
        }

        const remainingChildren = Math.max(j - option.count, 0);
        const numberOfChildrenTaken = Math.min(j, option.count);
        const numberOfAdultsTaken = option.count - numberOfChildrenTaken;
        const effectiveSpecialPrice = option.specialPrice ?? option.price;

        if (
          pricingType === 'byPerson' &&
          numberOfChildrenTaken > 0 &&
          remainingParticipants >= 0
        ) {
          const childPrice = numberOfChildrenTaken * effectiveSpecialPrice;
          const adultPrice = numberOfAdultsTaken * option.price;
          const newPrice =
            pricesMemo[remainingParticipants][remainingChildren] +
            childPrice +
            adultPrice;

          if (newPrice <= pricesMemo[i][j]) {
            pricesMemo[i][j] = newPrice;
            const childPartFormula =
              numberOfChildrenTaken > 0
                ? numberOfChildrenTaken > 1
                  ? `${numberOfChildrenTaken} × ${effectiveSpecialPrice}`
                  : `${effectiveSpecialPrice}`
                : '';
            const adultPartFormula =
              numberOfAdultsTaken > 0
                ? numberOfAdultsTaken > 1
                  ? `${numberOfAdultsTaken} × ${option.price}`
                  : `${option.price}`
                : '';
            const partFormula = `${adultPartFormula}${adultPartFormula && childPartFormula ? ' + ' : ''}${childPartFormula}`;
            formulaTracker[i][j] =
              `${formulaTracker[remainingParticipants][remainingChildren]}${formulaTracker[remainingParticipants][remainingChildren] ? ' + ' : ''}${partFormula}`;
          }
        }
      }
    }
  }

  const minimumPrice = pricesMemo[safeNbParticipants][safeNbChildren];
  const formula = formulaTracker[safeNbParticipants][safeNbChildren];

  if (minimumPrice === Infinity)
    return {
      formula: '',
      minimumPrice: 0,
    };

  return { minimumPrice, formula };
};

export const findMinimumPriceForDays = ({
  nbParticipants,
  nbDays,
  partitions,
  maxCapacity,
}: {
  nbParticipants: number;
  maxCapacity: number;
  nbDays: number;
  partitions: TarificationFormData['pricePartitions'];
}): PriceWithFormula => {
  const pricesMemo = new Array(nbDays + 1).fill(Infinity);
  const formulaTracker = new Array(nbParticipants + 1).fill('');

  pricesMemo[0] = 0;
  formulaTracker[0] = '';

  const options: Array<{
    duration: number;
    price: number;
    start: number;
    end: number;
  }> = [];
  partitions.forEach((partition) => {
    for (let i = partition.from; i <= partition.to; i++) {
      options.push({
        duration: i,
        price: partition.price,
        start: partition.from,
        end: partition.to,
      });
    }
  });

  for (let d = 1; d <= nbDays; d++) {
    for (const option of options) {
      if (d >= option.duration) {
        const price = option.price;
        const realDuration = 1; // advance by a single day to ensure price is applied correctly
        if (
          (d % option.end >= option.start || d % option.end === 0) && // If a special price exists between days 3 and 4, it should not be applied to days 5 and 6 but only 7 and 8 and so on
          pricesMemo[d] > pricesMemo[d - realDuration] + price * realDuration
        ) {
          pricesMemo[d] = pricesMemo[d - realDuration] + price * realDuration;
          formulaTracker[d] = formulaTracker[d - realDuration]
            ? `${formulaTracker[d - realDuration]} + ${option.price} × ${realDuration}`
            : `${option.price} × ${realDuration}`;
        }
      }
    }
  }

  const minimumDayCost = pricesMemo[nbDays];
  let formula = formulaTracker[nbDays];

  if (minimumDayCost === Infinity) return { formula: '', minimumPrice: 0 };

  const totalGroups = Math.ceil(nbParticipants / maxCapacity);
  const totalPrice = minimumDayCost * totalGroups;
  formula = totalGroups > 1 ? `${totalGroups} × (${formula})` : formula;

  return { formula, minimumPrice: totalPrice };
};
